import {MISC_KEYS} from "@/api/miscApi";
import {useUpdateUserLanguageMutation} from "@/api/userApi";
import CancelApplicationModal from "@/components/Campaign/CancelApplicationModal";
import ExtendCampaignModal from "@/components/Campaign/ExtendCampaignModal";
import CreateCampaignModal from "@/components/Campaign/management/CreateCampaignModal";
import CampaignDeleteDialog from "@/components/Campaign/management/campaignOverview/CampaignDeleteDialog";
import Carousel from "@/components/Campaign/management/components/Carousel";
import FullPageLoader from "@/components/Common/FullPageLoader";
import IENotSupportedModal from "@/components/Common/IENotSupportedModal";
import PaymentFailedModal from "@/components/Common/PaymentFailedModal";
import Footer from "@/components/Footer";
import AgencyInviteModal from "@/components/MultiAccount/AgencyInviteModal";
import EditCompanyMemberModal from "@/components/MultiAccount/EditCompanyMemberModal";
import InviteModal from "@/components/MultiAccount/InviteModal";
import RequestPermissionsModal from "@/components/MultiAccount/RequestPermissionsModal";
import WelcomeToMultiAccountModal from "@/components/MultiAccount/WelcomeToMultiAccountModal";
import NavBar from "@/components/NavBar/NavBar";
import DisciplinaryModal from "@/components/discipline/DisciplinaryModal";
import SingleDisciplineModal from "@/components/discipline/SingleDisciplineModal";
import FloatingBuyoutMenu from "@/components/plans/FloatingBuyoutMenu";
import {useDateConfig} from "@/hooks/useDateConfig";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useInternalIntercom} from "@/hooks/useInternalIntercom";
import useLocationEffect from "@/hooks/useLocationEffect";
import {usePosthogIdentify} from "@/hooks/usePosthogIdentify";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsAdmin, useIsCompany, useIsLoggedIn} from "@/hooks/useUserHooks";
import ErrorPage from "@/pages/ErrorPage";
import UnderMaintenancePage from "@/pages/UnderMaintenancePage";
import CreateCustomerModal from "@/pages/agencies/CreateCustomerModal";
import AddMoreBuyoutsModal from "@/pages/plans/AddMoreBuyoutsModal";
import AddMoreInvitesModal from "@/pages/plans/AddMoreInvitesModal";
import RequestMoreModal from "@/pages/plans/RequestMoreModal";
import RegistrationModal from "@/pages/registerCompany/RegistrationModal";
import {Can} from "@/permissions";
import {useAdminCarouselStore, useCarouselStore} from "@/store/carousel";
import {useCategoryStore} from "@/store/category";
import {LANGUAGE_OPTIONS} from "@/store/constants";
import {
  acceptLanguageAtom,
  campaignDeleteAtom,
  cancelApplicationModalDataAtom,
  createCampaignForCompanyAtom,
  createCustomerModalOpenAtom,
  editCompanyMemberModalAtom,
  extendCampaignModalDataAtom,
  isAdminFeedbackModalOpenAtom,
  isAgencyInviteModalOpenAtom,
  isDisciplinaryModalOpenAtom,
  isFinishRegistrationModalVisibleAtom,
  isMoreBuyoutsModalOpenAtom,
  isMoreInviteModalOpenAtom,
  isRequestMoreResourcesModalOpenAtom,
  isRequestPermissionsModalOpenAtom,
  isSingleDisciplineModalOpenAtom,
  isWelcomeToMultiAccountModalOpenAtom,
  languagePreferenceAtom,
  multiAccountInviteModalAtom,
  paymentFailedModalDataAtom,
} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TInterest, TLanguage} from "@/store/types";
import useUserStore from "@/store/user";
import {CookieBanner} from "@palmabit/react-cookie-law";
import {useQuery} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import dayjs from "dayjs";
import {useAtomValue, useSetAtom} from "jotai";
import Cookies from "js-cookie";
import {Suspense, useEffect, useMemo, useRef, type FC} from "react";
import {ErrorBoundary} from "react-error-boundary";
import ReactPixel from "react-facebook-pixel";
import ReactGA from "react-ga4";
import {hotjar} from "react-hotjar";
import {useTranslation} from "react-i18next";
import {Outlet, ScrollRestoration, useLocation} from "react-router";
import {useEvent} from "react-use";

const Layout: FC = () => {
  const {t, i18n} = useTranslation();
  const {error} = useFetchUser();
  const location = useLocation();
  const isLoggedIn = useIsLoggedIn();
  const user = useUserStore(state => state.user);
  const isAdmin = useIsAdmin();
  const isCompany = useIsCompany();
  const categories = useCategoryStore(state => state.categories);
  const adminCategories = useCategoryStore(state => state.adminCategories);
  const setCategoryValue = useCategoryStore(state => state.setter);
  useEvent("wheel", _ => {
    // Prevent scrolling on wheel event in input[type="number"]
    const input = document.activeElement as HTMLInputElement;
    if (input.type === "number") {
      input.blur();
    }
  });
  useInternalIntercom();
  useLocationEffect();
  usePosthogIdentify();
  useDateConfig();
  const isMarketingScriptLoaded = useRef(false);
  const isStatisticsScriptLoaded = useRef(false);

  const setAcceptLanguage = useSetAtom(acceptLanguageAtom);
  const storedLanguage = useAtomValue(languagePreferenceAtom);
  const isAgencyInviteModalOpen = useAtomValue(isAgencyInviteModalOpenAtom);
  const isMoreInviteModalOpen = useAtomValue(isMoreInviteModalOpenAtom);
  const isMoreBuyoutsModalOpen = useAtomValue(isMoreBuyoutsModalOpenAtom);
  const isSingleDisciplineModalOpen = useAtomValue(isSingleDisciplineModalOpenAtom);
  const isRequestMoreResourcesOpenAtom = useAtomValue(isRequestMoreResourcesModalOpenAtom);
  const paymentFailedModalData = useAtomValue(paymentFailedModalDataAtom);
  const companyForCampaignCreation = useAtomValue(createCampaignForCompanyAtom);
  const isFinishRegistrationModalOpen = useAtomValue(isFinishRegistrationModalVisibleAtom);
  const isCreateCustomerModalOpen = useAtomValue(createCustomerModalOpenAtom);
  const isDisciplinaryModalOpen = useAtomValue(isDisciplinaryModalOpenAtom);
  const editCompanyMemberModal = useAtomValue(editCompanyMemberModalAtom);
  const isWelcomeToMultiAccountModalOpen = useAtomValue(isWelcomeToMultiAccountModalOpenAtom);
  const multiAccountInviteModal = useAtomValue(multiAccountInviteModalAtom);
  const isRequestPermissionModalOpen = useAtomValue(isRequestPermissionsModalOpenAtom);
  const campaignToBeDeleted = useAtomValue(campaignDeleteAtom);
  const isAdminFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const extendCampaignModalData = useAtomValue(extendCampaignModalDataAtom);
  const cancelApplicationModalData = useAtomValue(cancelApplicationModalDataAtom);
  const carouselStore = isAdminFeedbackModalOpen ? useAdminCarouselStore : useCarouselStore;
  const isCarouselOpen = carouselStore(state => state.isOpen);
  const isIEModalOpen = carouselStore(state => state.isIENotSupportedModalOpen);
  const isUnderMaintenance = useMemo(() => {
    const maintenanceDeadline = import.meta.env.VITE_APP_UNDER_MAINTENANCE_UNTIL_UTC;

    return !!maintenanceDeadline && dayjs().isBefore(maintenanceDeadline);
  }, []);
  const isBackendDown = useMemo(() => error?.code === "ERR_NETWORK", [error?.code]);
  const isOnVerifyEmailPage = useMemo(() => location.pathname.startsWith("/verify-email"), [location.pathname]);

  const {data: categoriesData} = useQuery<
    AxiosResponse<{
      maximum_allowed_category: number;
      results: TInterest[];
      version: number;
    }>
  >({
    queryKey: MISC_KEYS.qCategoryList,
    enabled: !categories.length,
  });
  const {data: adminCategoriesData} = useGenericQuery<TInterest[]>({
    queryKey: MISC_KEYS.qCategoryListAdmin,
    enabled: isAdmin && !adminCategories.length,
  });
  const {mutate: updateLanguage} = useUpdateUserLanguageMutation();

  useEffect(() => {
    const cookie = Cookies.get("isLoggedIn");

    if (isLoggedIn && !cookie) {
      Cookies.set("isLoggedIn", "true", {expires: 365});
    }
  }, [isLoggedIn]);
  useEffect(() => {
    setCategoryValue(state => {
      state.categories = categoriesData?.data.results ?? [];
      state.maxAllowedCategories = categoriesData?.data.maximum_allowed_category!;
    });
  }, [categoriesData]);
  useEffect(() => {
    setCategoryValue(state => {
      state.adminCategories = adminCategoriesData ?? [];
    });
  }, [adminCategoriesData]);
  useEffect(() => {
    const browserLanguage: TLanguage = navigator.language.startsWith("de") ? "de_CH" : "en_EN";
    const userLanguage = user.language_code;
    const preferredLanguageOption = LANGUAGE_OPTIONS.find(
      lang => lang.code === (userLanguage ?? storedLanguage ?? browserLanguage),
    );

    i18n.changeLanguage(preferredLanguageOption?.i18nextCode);
    setAcceptLanguage(preferredLanguageOption?.i18nextCode === "ger" ? "de" : preferredLanguageOption?.i18nextCode!);
    useRegistrationStore.getState().setValue("languageCode", preferredLanguageOption?.code);

    if (user.id && userLanguage !== preferredLanguageOption?.code) {
      updateLanguage({
        lang: LANGUAGE_OPTIONS.find(lang => lang.i18nextCode === i18n.resolvedLanguage)?.code as TLanguage,
      });
    }
  }, [storedLanguage, user.id, user.language_code, updateLanguage]);

  return (
    <div className="font-roboto flex min-h-screen flex-col justify-between">
      <ScrollRestoration />
      <Can I="create" a="BuyoutPurchase">
        {!isCompany && <FloatingBuyoutMenu type="campaigns" />}
        <FloatingBuyoutMenu />
      </Can>
      <CookieBanner
        className="top-[unset] bottom-0"
        message={t("cookieBanner")}
        policyLink="https://refluenced.ch/cookie-policy/"
        privacyPolicyLinkText={t("privacy_policy")}
        necessaryOptionText={t("Necessary")}
        preferencesOptionText={t("Preferences")}
        statisticsOptionText={t("Statistics")}
        marketingOptionText={t("Marketing")}
        acceptButtonText={t("Accept All")}
        acceptSelectionButtonText={t("Accept Selection")}
        showDeclineButton={true}
        dismissOnScroll={false}
        showPreferencesOption={true}
        showStatisticsOption={true}
        showMarketingOption={true}
        preferencesDefaultChecked={true}
        statisticsDefaultChecked={true}
        marketingDefaultChecked={true}
        wholeDomain={true}
        onAccept={() => {
          document.cookie = `${document.cookie}; rcl_consent_given=true; path=/`;
        }}
        onAcceptStatistics={() => {
          if (isStatisticsScriptLoaded.current) {
            return;
          }

          isStatisticsScriptLoaded.current = true;

          hotjar.initialize(2510205, 6);
          ReactGA.initialize(import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TRACKING_ID ?? "G-BGK6J4X5N9");
          (window as Record<string, any>)[
            `ga-disable-${import.meta.env.VITE_APP_GOOGLE_ANALYTICS_TRACKING_ID ?? "G-BGK6J4X5N9"}`
          ] = false;

          ReactPixel.init("1371835213256071", undefined, {autoConfig: true, debug: false});
          ReactPixel.pageView();
        }}
        onAcceptMarketing={() => {
          if (isMarketingScriptLoaded.current) {
            return;
          }

          isMarketingScriptLoaded.current = true;

          const gtm = document.createElement("script");
          gtm.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-K4Z8MS5');`;
          document.head.appendChild(gtm);

          const gtm_noscript = document.createElement("noscript");
          gtm_noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-K4Z8MS5"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
          document.body.insertBefore(gtm_noscript, document.body.firstChild);
        }}
      />
      <NavBar shouldOnlyShowLogo={isUnderMaintenance || isOnVerifyEmailPage} />
      {isFinishRegistrationModalOpen && <RegistrationModal />}
      {isCreateCustomerModalOpen && <CreateCustomerModal />}
      {isDisciplinaryModalOpen && <DisciplinaryModal />}
      {editCompanyMemberModal.isVisible && <EditCompanyMemberModal />}
      {paymentFailedModalData && <PaymentFailedModal />}
      {isMoreInviteModalOpen && <AddMoreInvitesModal />}
      {isMoreBuyoutsModalOpen.show && <AddMoreBuyoutsModal />}
      {isSingleDisciplineModalOpen && <SingleDisciplineModal />}
      {isRequestMoreResourcesOpenAtom.show && <RequestMoreModal />}
      {!!companyForCampaignCreation && <CreateCampaignModal />}
      {!!campaignToBeDeleted && <CampaignDeleteDialog />}
      {isIEModalOpen && <IENotSupportedModal />}
      {isCarouselOpen && <Carousel />}
      {isWelcomeToMultiAccountModalOpen && <WelcomeToMultiAccountModal />}
      {multiAccountInviteModal.isVisible && <InviteModal />}
      {isRequestPermissionModalOpen && <RequestPermissionsModal />}
      {isAgencyInviteModalOpen && <AgencyInviteModal />}
      {extendCampaignModalData && <ExtendCampaignModal />}
      {cancelApplicationModalData && <CancelApplicationModal />}
      <main
        lang={i18n.resolvedLanguage === "ger" ? "de" : i18n.resolvedLanguage}
        className="flex grow flex-col [&>div]:w-full">
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <Suspense fallback={<FullPageLoader />}>
            {isUnderMaintenance || isBackendDown ? <UnderMaintenancePage /> : <Outlet />}
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
